import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

function ExportStep({ sessionId, editedData, sessionData, onReset }) {
  const [exporting, setExporting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      // Builds the XLSX only; the zip is then a normal browser download
      // (streams to disk with the browser's own progress, no size/time limit)
      const res = await axios.post(`${API}/api/export/${sessionId}`, editedData, {
        timeout: 600000
      });
      const projectCode = editedData?.project?.projectCode || 'export';
      const a = document.createElement('a');
      a.href = `${API}${res.data.downloadUrl}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setSummary({
        projectCode,
        products: editedData?.products?.length || 0,
        tracks: editedData?.tracks?.length || 0,
        audio: sessionData?.audioFiles?.length || 0,
        artwork: sessionData?.artworkFiles?.length || 0
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const missingISRCs = (editedData?.tracks || []).filter(t => !t.isrc);
  const warnings = missingISRCs.map(t => `Track #${t.trackNumber} (${t.productCode}) — no ISRC (new track)`);

  if (done) {
    return (
      <Page>
        <Card>
          <DoneIcon><CheckCircleIcon /></DoneIcon>
          <DoneTitle>Export complete</DoneTitle>
          <DoneSub>Your Orchard package is downloading — check your browser's downloads</DoneSub>
          <SummaryBox>
            {[
              ['Project', summary?.projectCode, true],
              ['Products', `${summary?.products} variants`],
              ['Tracks', `${summary?.tracks} tracks`],
              ['Audio Files', `${summary?.audio} .wav files`],
              ['Artwork', `${summary?.artwork} converted JPEG(s)`],
            ].map(([label, value, mono]) => (
              <SummaryRow key={label}>
                <SummaryLabel>{label}</SummaryLabel>
                <SummaryValue mono={mono}>{value}</SummaryValue>
              </SummaryRow>
            ))}
          </SummaryBox>
          <ResetBtn onClick={onReset}>Start new build</ResetBtn>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <CardTop>
          <Heading>Ready to export</Heading>
          <Subheading>Review the summary below then download your Orchard import package</Subheading>
        </CardTop>

        <SummaryBox>
          <SummarySection>
            <SectionTitle>Package contents</SectionTitle>
            {[
              ['Project Code', editedData?.project?.projectCode || '—', true],
              ['Project Name', editedData?.project?.projectName || '—'],
              ['Products', `${editedData?.products?.length || 0} variants`],
              ['Tracks', `${editedData?.tracks?.length || 0} tracks`],
              ['Contributors', `${(editedData?.contributors?.length || 0) + (editedData?.otherContributors?.length || 0)}`],
              ['Audio Files', `${sessionData?.audioFiles?.length || 0} .wav files`],
              ['Artwork', `${sessionData?.artworkFiles?.filter(a => a.wasConverted).length || 0} converted JPEG(s)`],
            ].map(([label, value, mono]) => (
              <SummaryRow key={label}>
                <SummaryLabel>{label}</SummaryLabel>
                <SummaryValue mono={mono}>{value}</SummaryValue>
              </SummaryRow>
            ))}
          </SummarySection>

          <SummarySection>
            <SectionTitle>Products</SectionTitle>
            {(editedData?.products || []).map((p, i) => (
              <ProductRow key={i}>
                <PCode>{p.productCode}</PCode>
                <PLang>{p.language || p.langCode}</PLang>
                <PName>{p.productName}</PName>
              </ProductRow>
            ))}
          </SummarySection>
        </SummaryBox>

        {warnings.length > 0 && (
          <WarningBox>
            <WarningTitle>{warnings.length} warning{warnings.length > 1 ? 's' : ''}</WarningTitle>
            {warnings.map((w, i) => <WarningItem key={i}>{w}</WarningItem>)}
          </WarningBox>
        )}

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <ExportBtn onClick={handleExport} disabled={exporting}>
          {exporting ? <><Spinner />Building package...</> : <><DownloadIcon />Download Orchard package</>}
        </ExportBtn>

        <ExportNote>Downloads as .zip containing: Orchard_Import.xlsx + renamed audio files + converted artwork</ExportNote>
      </Card>
    </Page>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="9.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6.8 11.2l2.7 2.7 5.5-5.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M8 2.5v8M4.8 7.3L8 10.5l3.2-3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 12.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 60px);
  padding: 40px 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 560px;
  background: var(--bg-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 32px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CardTop = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Heading = styled.h1`
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.3px;
  margin-bottom: 6px;
`;

const Subheading = styled.p`
  color: var(--text-2);
  font-size: 13px;
`;

const SummaryBox = styled.div`
  width: 100%;
  background: var(--bg-3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px 20px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const SummarySection = styled.div``;

const SectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-3);
  margin-bottom: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  color: var(--text-2);
`;

const SummaryValue = styled.span`
  font-size: 12px;
  color: var(--text);
  font-weight: 600;
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'var(--font-ui)'};
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
`;

const PCode = styled.span`
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--accent-text);
  background: var(--accent-dim);
  padding: 2px 8px;
  border-radius: var(--radius);
  min-width: 70px;
  text-align: center;
`;

const PLang = styled.span`
  font-size: 10px;
  color: var(--text-2);
  background: var(--bg-4);
  border: 1px solid var(--border-2);
  padding: 1px 7px;
  border-radius: var(--radius);
  font-weight: 600;
`;

const PName = styled.span`
  font-size: 12px;
  color: var(--text);
  flex: 1;
  font-weight: 500;
`;

const WarningBox = styled.div`
  width: 100%;
  background: var(--amber-dim);
  border: 1px solid var(--amber);
  border-radius: var(--radius);
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const WarningTitle = styled.p`
  font-size: 12px;
  color: var(--amber);
  font-weight: 600;
  margin-bottom: 6px;
`;

const WarningItem = styled.p`
  font-size: 11px;
  color: var(--amber);
  padding: 2px 0;
  &::before { content: '→ '; }
`;

const ErrorMsg = styled.div`
  width: 100%;
  padding: 10px 14px;
  background: var(--red-dim);
  border: 1px solid var(--red);
  border-radius: var(--radius);
  color: var(--red);
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const ExportBtn = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--accent);
  color: #ffffff;
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 13px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ExportNote = styled.p`
  font-size: 11px;
  color: var(--text-3);
  text-align: center;
  line-height: 1.5;
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const DoneIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--green-dim);
  color: var(--green);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const DoneTitle = styled.h1`
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.3px;
  margin-bottom: 6px;
`;

const DoneSub = styled.p`
  color: var(--text-2);
  font-size: 13px;
  margin-bottom: 20px;
`;

const ResetBtn = styled.button`
  margin-top: 12px;
  padding: 10px 24px;
  background: var(--bg-3);
  color: var(--text);
  border: 1px solid var(--border);
  font-family: var(--font-ui);
  font-weight: 500;
  font-size: 13px;
  border-radius: var(--radius);
  transition: all 0.12s;
  &:hover { background: var(--bg-4); border-color: var(--border-2); }
`;

export default ExportStep;
